<template>
  <div class="text-field-standard-box" :class="{ 'text-field-standard-box__search': searchInput }">
    <span
      class="text-field-standard-box__label"
      :class="{ 'text-field-standard-box__label__disabled': disabled }"
    >
      {{ label }} <span v-if="required" class="required">*</span>
      <TooltipStandardV2
        v-if="infoText"
        :text="infoText"
        :arrowPosition="'bottom-center'"
        :vertical-offset="2"
      >
        <IconInfoCircle></IconInfoCircle>
      </TooltipStandardV2>
    </span>
    <div class="wrapper">
      <IconSearch v-if="searchInput" class="input-icon-left" />
      <input
        ref="inputRef"
        v-model="inputValue"
        :type="type"
        class="text-field-standard"
        :disabled="disabled"
        :class="{
          'text-field-standard__date': type === 'date',
          'text-field-standard__error': error,
          'text-field-standard__success': success,
          'text-field-standard__disabled': disabled,
          'text-field-standard__search': searchInput
        }"
        :placeholder="placeHolderText"
        @blur="emits('emitBlur')"
        @input="emitInputValue"
      />
      <span
        class="input-icon"
        :class="{ 'input-icon__error': error, 'input-icon__success': success }"
      >
        <IconAlertCircle v-if="error" class="input-icon__icon-error" />
        <IconCalendar
          v-if="type === 'date'"
          class="input-icon__icon-date"
          :class="{ 'input-icon__icon-date__disabled': disabled }"
          role="button"
          :tabindex="disabled ? -1 : 0"
          @click="openCalendar"
          @keydown.enter="openCalendar"
          @keydown.space="openCalendar"
        />
        <IconCheckCircle v-if="success" class="input-icon__icon-success" />
        <IconX
          v-if="showIconXValue !== '' && !disabled"
          class="input-icon__icon-x"
          role="button"
          tabindex="0"
          @click="clearInputvalue"
          @keydown.enter="clearInputvalue"
          @keydown.space="clearInputvalue"
        />
      </span>
    </div>
    <span
      class="text-field-standard-box__help-text"
      :class="{
        'text-field-standard-box__help-text__disabled': disabled,
        'text-field-standard-box__help-text__error': error
      }"
    >
      {{ error ? errorMessage : helpText }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import IconAlertCircle from '@/design-system/assets/icons/IconAlertCircle.vue'
import IconCalendar from '@/design-system/assets/icons/IconCalendar.vue'
import IconCheckCircle from '@/design-system/assets/icons/IconCheckCircle.vue'
import IconSearch from '@/design-system/assets/icons/IconSearch.vue'
import IconX from '@/design-system/assets/icons/IconX.vue'
import { toRefs, ref, watch, onMounted } from 'vue'
import TooltipStandardV2 from '../atoms/TooltipStandardV2.vue'
import IconInfoCircle from '../assets/icons/IconInfoCircle.vue'

const props = withDefaults(
  defineProps<{
    clearInput?: boolean
    disabled?: boolean
    error?: boolean
    errorMessage?: string
    helpText?: string
    hideIconX?: boolean
    infoText?: string
    label?: string
    numberValue?: number
    placeHolderText?: string
    prefilledText?: string
    required?: boolean
    searchInput?: boolean
    success?: boolean
    type?: string
    focus?: boolean
  }>(),
  {
    clearInput: false,
    disabled: false,
    error: false,
    errorMessage: '',
    helpText: '',
    hideIconX: false,
    infoText: '',
    label: '',
    numberValue: 0,
    placeHolderText: '',
    prefilledText: '',
    required: false,
    searchInput: false,
    success: false,
    type: 'text',
    focus: false
  }
)
const { clearInput } = toRefs(props)
interface EMITS {
  (e: 'emitInput', inputValue: string | number): void
  (e: 'emitBlur')
  (e: 'emitClearInput')
}
const emits = defineEmits<EMITS>()
const inputRef = ref<HTMLInputElement | null>(null)
const inputValue = ref<string | number>('')
const prefilledText = ref<string>(props.prefilledText)
const showIconXValue = ref<string>('')

watch(
  () => props.numberValue,
  (newVal) => {
    inputValue.value = newVal
  }
)
function clearInputvalue() {
  showIconXValue.value = ''
  emits('emitClearInput')
  if (!props.prefilledText.length) inputValue.value = ''
  inputRef.value?.focus()
}
function emitInputValue() {
  emits('emitInput', inputValue.value)
}
function hideIconX() {
  showIconXValue.value = ''
}
function openCalendar() {
  if (!props.disabled && inputRef.value) inputRef.value.showPicker()
}
function showIconX() {
  showIconXValue.value = 'show'
}
watch(clearInput, (newVal) => {
  if (newVal == true) {
    clearInputvalue()
    showIconXValue.value = ''
    inputRef.value?.blur()
    emitInputValue()
  }
})
watch(inputValue, (newVal) => {
  if (typeof newVal === 'string' && newVal.length >= 1 && !props.hideIconX) showIconX()
  else hideIconX()
})
if (prefilledText.value?.length) inputValue.value = prefilledText.value
watch(prefilledText, (newVal) => {
  if (newVal?.length) inputValue.value = newVal
})
onMounted(() => {
  if (prefilledText.value?.length) inputValue.value = prefilledText.value
  if (props.type === 'number' && props.numberValue !== undefined)
    inputValue.value = props.numberValue

  if (props.focus) inputRef.value?.focus()
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.wrapper {
  position: relative;
}
.input-icon {
  position: absolute;
  right: variables.$position-xlarge;
  top: calc(50% - 0.9em); /* Keep icon in center of input, regardless of the input height */
  &__icon-x {
    color: variables.$color-primary;
    cursor: pointer;
  }
  &__icon-date {
    color: variables.$color-primary;
    &:hover {
      cursor: pointer;
    }
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: auto;
    }
    &__disabled {
      color: variables.$color-greyscale-secondary;
      &:hover {
        cursor: not-allowed;
      }
    }
  }
  &__icon-error {
    color: variables.$color-error;
  }
  &__icon-success {
    color: variables.$color-success;
  }
  &-left {
    left: variables.$position-xlarge;
    margin-top: -#{variables.$margin-large};
    position: absolute;
    top: 50%;
  }
}
.text-field-standard {
  @include font-mixins.desktop-bold-paragraph;
  border-radius: variables.$border-radius-medium;
  border: variables.$border-width-xsmall solid variables.$color-greyscale-secondary;
  color: variables.$color-greyscale-content;
  gap: variables.$gap-large;
  height: 48px;
  padding: calc(variables.$padding-medium - 1px) variables.$padding-xlarge;
  width: 100%;
  &:focus {
    border: variables.$border-width-xsmall solid variables.$color-primary;
    outline: variables.$border-width-medium solid variables.$color-primary-light;
    // Outline workaround for iPhone/Safari
    @supports (-webkit-overflow-scrolling: touch) {
      box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
      outline: none;
      -webkit-appearance: none;
    }
    // Outline workaround for iPad/Safari
    @media not all and (min-resolution: 0.001dpcm) {
      box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
      outline: none;
      -webkit-appearance: none;
    }
  }
  &__date {
    &::-webkit-inner-spin-button,
    &::-webkit-calendar-picker-indicator {
      display: none;
      opacity: 0;
      -webkit-appearance: none;
    }
  }
  &__error {
    border: variables.$border-width-xsmall solid variables.$color-error;
    &:focus {
      border: variables.$border-width-xsmall solid variables.$color-error;
      outline: variables.$border-width-medium solid variables.$color-error-outline;
    }
  }
  &__success {
    border: variables.$border-width-xsmall solid variables.$color-success;
  }
  &__disabled {
    background: variables.$color-greyscale-background;
    color: variables.$color-greyscale-secondary;
    &:hover {
      cursor: not-allowed;
    }
  }
  &__search {
    text-indent: calc(2 * #{variables.$padding-xlarge});
    text-overflow: ellipsis;
    width: 100%;
    padding-right: calc(5 * #{variables.$padding-medium});
    @include font-mixins.desktop-regular-paragraph;
  }
}
.text-field-standard-box {
  display: flex;
  flex-direction: column;
  gap: variables.$gap-large;
  &__label {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    display: flex;
    flex-direction: row;
    width: fit-content;
    gap: variables.$gap-large;
    svg {
      width: variables.$tooltip-icon-size;
    }
    &__disabled {
      color: variables.$color-greyscale-secondary;
    }
    & .required {
      color: variables.$color-error;
      margin-left: -4px;
    }
  }
  &__help-text {
    @include font-mixins.desktop-bold-label;
    &__disabled {
      color: variables.$color-greyscale-secondary;
    }
    &__error {
      color: variables.$color-error;
    }
  }
  &__search {
    width: 100%;
  }
}
</style>
